/**
 * Console breadcrumbs for one "save capture" attempt.
 *
 * Off by default. Enable for a session without a rebuild:
 *
 *   debugCaptureSave(true)   // persist via localStorage, then save again
 *   debugCaptureSave(false)  // turn off
 *
 * Or set ``localStorage.debugCaptureSave = '1'`` and reload. A build-time
 * ``NEXT_PUBLIC_DEBUG_CAPTURE_SAVE=1`` also turns them on.
 *
 * A save spans three cell calls — the provenance origin-MD5 lookup, the
 * junkdrawer presign, and the S3 PUT — then queues a debounced favorites write,
 * across ``mediaDisplay`` and ``userImageStorage``. Intermittent failures were
 * hard to place because each layer logged (or swallowed) its own error with
 * nothing tying the lines to one attempt, and several give-up paths returned
 * ``false`` silently. Every line here carries the same ``attempt`` id so a
 * single save reads as one sequence, and each failure names the stage it died
 * at plus the HTTP status — ``status: null`` is the signature of the
 * CORS-opaque cell rejection that ``cors-auth-recovery`` tries to refresh past.
 *
 * Never pass image bytes, capture content, or presigned URLs (those carry
 * credentials) — identifiers, sizes and outcomes only.
 */

export const CAPTURE_SAVE_SCOPE = '[capture-save]';
export const CAPTURE_SAVE_DEBUG_STORAGE_KEY = 'debugCaptureSave';

const SCOPE = CAPTURE_SAVE_SCOPE;

type CaptureSaveLogLevel = 'info' | 'warn' | 'error';

/** Steps of a save, in the order a successful attempt passes through them. */
export type CaptureSaveStage =
  | 'resolve-case'
  | 'origin-md5'
  | 'presign'
  | 's3-put'
  | 'attach-to-media'
  | 'favorites-queue'
  | 'favorites-persist';

export type CaptureSaveDetails = Record<string, unknown>;

export interface CaptureSaveLog {
  /** Correlates every line of one attempt. */
  readonly attempt: string;
  /** A stage completed. */
  step(stage: CaptureSaveStage, details?: CaptureSaveDetails): void;
  /** A stage was deliberately not run, or its result was discarded. */
  skipped(stage: CaptureSaveStage, reason: string, details?: CaptureSaveDetails): void;
  /** A stage failed. */
  failed(stage: CaptureSaveStage, error: unknown, details?: CaptureSaveDetails): void;
  /** The capture is saved and attached. */
  done(details?: CaptureSaveDetails): void;
}

const NOOP_LOG: CaptureSaveLog = {
  attempt: '#0',
  step() {},
  skipped() {},
  failed() {},
  done() {},
};

function envFlagEnabled(): boolean {
  try {
    return process.env.NEXT_PUBLIC_DEBUG_CAPTURE_SAVE === '1';
  } catch {
    return false;
  }
}

function storedFlagEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = window.localStorage.getItem(CAPTURE_SAVE_DEBUG_STORAGE_KEY);
    return stored === '1' || stored === 'true';
  } catch {
    return false;
  }
}

/** True when capture-save breadcrumbs should print. */
export function isCaptureSaveLogEnabled(): boolean {
  return envFlagEnabled() || storedFlagEnabled();
}

/**
 * Persist the console toggle. Called as ``debugCaptureSave(true)`` from DevTools
 * once this module has loaded (any capture save, or a page that imports the
 * helper). Returns the resulting enabled state.
 */
export function setCaptureSaveLogEnabled(on: boolean): boolean {
  if (typeof window === 'undefined') return envFlagEnabled();
  try {
    if (on) {
      window.localStorage.setItem(CAPTURE_SAVE_DEBUG_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(CAPTURE_SAVE_DEBUG_STORAGE_KEY);
    }
  } catch {
    /* private mode */
  }
  return isCaptureSaveLogEnabled();
}

function installConsoleToggle(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { debugCaptureSave?: (on?: boolean) => boolean };
  if (w.debugCaptureSave) return;
  w.debugCaptureSave = (on?: boolean) => {
    if (on === undefined) return isCaptureSaveLogEnabled();
    return setCaptureSaveLogEnabled(on);
  };
}

installConsoleToggle();

export function logCaptureSave(
  level: CaptureSaveLogLevel,
  message: string,
  details?: CaptureSaveDetails,
): void {
  if (!isCaptureSaveLogEnabled()) return;
  const line = `${SCOPE} ${message}`;
  if (details === undefined) {
    console[level](line);
    return;
  }
  console[level](line, details);
}

/**
 * Flatten an error for logging. ``status`` is kept even when ``null`` because
 * the distinction matters: a number is a readable rejection, ``null`` means the
 * response had no CORS headers and the browser could not read one.
 */
export function describeCaptureSaveError(error: unknown): CaptureSaveDetails {
  const value = error as
    | { message?: unknown; name?: unknown; status?: unknown }
    | null
    | undefined;
  const status = value?.status;
  return {
    error: typeof value?.message === 'string' ? value.message : String(error),
    ...(typeof value?.name === 'string' ? { errorName: value.name } : {}),
    ...(typeof status === 'number' || status === null ? { status } : {}),
  };
}

/**
 * Compressed call stack of whoever issued a write, for the cases where knowing
 * *who* wrote matters more than what was written. Frame formats differ between
 * engines, so this filters out this module's own frames rather than slicing at
 * a fixed offset.
 */
export function captureSaveCallerStack(depth = 6): string {
  const lines = new Error().stack?.split('\n') ?? [];
  const frames = lines
    .map((line) => line.trim().replace(/^at\s+/, ''))
    // Match on this function's name, not the module path: bundlers rewrite the
    // filename but keep the symbol.
    .filter(
      (line) =>
        line !== '' && line !== 'Error' && !line.includes('captureSaveCallerStack'),
    );
  return frames.slice(0, depth).join(' <- ') || 'unavailable';
}

let attemptCounter = 0;

/**
 * Open a log for one save attempt and record its inputs. Callers that own the
 * whole flow should create this and hand it to {@link uploadUserImage} so the
 * upload stages join the same sequence; direct callers of the upload get their
 * own attempt. When logging is off this is a no-op so call sites stay cheap.
 */
export function startCaptureSave(details: CaptureSaveDetails = {}): CaptureSaveLog {
  if (!isCaptureSaveLogEnabled()) return NOOP_LOG;

  attemptCounter += 1;
  const attempt = `#${attemptCounter}`;
  const startedAt = Date.now();
  const withElapsed = (extra: CaptureSaveDetails = {}): CaptureSaveDetails => ({
    attempt,
    elapsedMs: Date.now() - startedAt,
    ...extra,
  });

  console.info(`${SCOPE} ${attempt} start`, withElapsed(details));

  return {
    attempt,
    step(stage, extra) {
      console.info(`${SCOPE} ${attempt} ${stage}`, withElapsed(extra));
    },
    skipped(stage, reason, extra) {
      console.warn(`${SCOPE} ${attempt} ${stage} skipped: ${reason}`, withElapsed(extra));
    },
    failed(stage, error, extra) {
      console.error(
        `${SCOPE} ${attempt} ${stage} failed`,
        withElapsed({ ...extra, ...describeCaptureSaveError(error) }),
      );
    },
    done(extra) {
      console.info(`${SCOPE} ${attempt} saved`, withElapsed(extra));
    },
  };
}
